import pathOr from 'ramda/src/pathOr';
import isEmpty from 'ramda/src/isEmpty';
import { stateGo, stateReload } from 'redux-ui-router';
import moment from 'moment';
import { fetchCodeTypes, fetchConsents } from 'invision-core/src/components/metadata/codes/codes.actions';
import { hasAccess, hasAdminAccess } from 'invision-core/src/components/security/permission.service';
import { MetadataCodeLoadedSelector, MetadataCodeTypeSelector, SubscriberCategoryRequirementsForCurrentBusinessUnitOrDefault, SubscriberRequirementsForCurrentBusinessUnitOrDefault } from 'invision-core/src/components/metadata/codes/codes.selectors';
import { RouteCurrentStateSelector, RouteParams } from 'invision-core/src/components/router/router.selectors';
import CoreLocaleKeys from 'invision-core/src/locales/core.locale.keys';
import i18n from 'invision-core/src/components/i18n/i18n';
import { CODES } from 'invision-core/src/components/metadata/codes/codes.constants';
import { IsDbss } from 'invision-core/src/components/session/businessunit.selectors';
import { UserSecurityAttributesSelector } from 'invision-core/src/components/session/session.selectors';
import { ARMWarningTranslatedMessageSelector } from 'invision-core/src/components/warnings/warnings.selectors';
import { VISIBLE_QUICK_ACTIONS } from 'invision-core/src/components/configurable/customercare';
import { INVALID_INVENTORY_REASON_CODES, INVALID_SHOPPING_CART_FAULT_CODE, HIERARCHY_MOVE_STATUS_TYPES, NOTIFICATION_TIME_LENGTH, ROOT_CARE_MODULE_ROUTE } from '../../customercare.constants';
import { CASES_DETAILS_STATE_REDIRECT } from './cases/cases.config';
import CustomerCareLocaleKeys from '../../locales/keys';
import { CanUserBypassResetSelector, CurrentCustomerNameSelector, CurrentCustomerResponsibilityAccountsItemsSelector, CurrentCustomerSelector, CurrentCustomerIdSelector, CustomerIsFetchingDataSelector, InstanceLevelOrderedSelector, InvalidInventoryItemSelector, IsPIIDataAccessEnabledSelector, IsWrappingCustomerSelector, OfferingIdSelector, OfferingInstanceIdSelector, SelectedCustomerConsentsMandatoryUnacceptedSelector, SelectedCustomerConsentsOptionalSelector, ShoppingCartErrorSelector, ShoppingCartSelector, ShowAutoPayBannerSelector, ShowDetailedTaxationPerformanceBannerSelector, StoreOrderErrorSelector, StoreOrderSelector, StoreOrderTypeSelector } from '../../reducers/selectors/customer.selectors';
import { ConvergentBillerAccountOfferingsSelector, CreditLimitBreachedSelector, CreditLimitSummarySelector, CurrentAccountSummarySelector } from '../../reducers/selectors/customer.convergent.biller.selectors';
import { HierarchyNameSelector, ResponsiblePartyCustomerSelector, ResponsiblePartyCustomerNameSelector } from '../../reducers/selectors/customer.account.hierarchy.selectors';
import { ChallengeAnswerSelector, HasSubscriberPasswordAccessSelector, IsFetchingChallengeAnswerSelector } from '../../reducers/selectors/customer.challenge.selectors';
import { CaseTypesSelector, FormattedInvoicesSelector } from '../../reducers/selectors/customer.cases.selectors';
import { cancelAnonymizeCustomer, cancelStoreOrder, clearSavedShoppingCart, customerResetDevicePin, customerResetPassword, customerResetRevokedPassword, customerWrapUp, fetchShoppingCart, fetchStoreOrder, resetCustomerRecoverable, retrieveCustomer, retrieveDevicePinStatus, setIsEditingProfile } from '../../reducers/actions/customer.actions';
import { setNavGroup } from '../customerActionsTemplate/customer.actions.template.actions';
import { retrieveCustomerChallengeAnswer } from '../../reducers/actions/customer.challenge.actions';
import { retrieveHierarchy, searchHierarchyNodes } from '../../reducers/actions/customer.account.hierarchy.actions';
import { CASE_ACCESS, RESET_SUBSCRIBER_PASSWORD_ACCESS, SUBSCRIBER_ADMIN_ACCESS } from '../../security.attributes';
import { CustomerActionsTemplateSelector,
// TODO: remove with InvEnableiPadOptimizedLayout cleanup
IpadOptimizedLayoutFeatureToggleSelector, InvEnableFinalInvoiceGenerationOnRootInvoiceNodeFeatureToggleSelector, IsProfilePageEnabledSelector } from '../../reducers/selectors/customer.actions.template.selectors';
import { CANCEL_STORE_ORDER_QUICK_ACTION, CHANGE_BILLING_TYPE_QUICK_ACTION, CLEAR_SAVED_CART_QUICK_ACTION, CREATE_COMMERCIAL_CHILD_QUICK_ACTION, CUSTOMER_WRAP_UP_QUICK_ACTION, DISCONNECT_CUSTOMER_QUICK_ACTION, MANAGE_SERVICES_QUICK_ACTION, NEW_DBSS_ORDER_QUICK_ACTION, NEW_PRODUCT_ORDER_QUICK_ACTION, OPEN_CASE_QUICK_ACTION, REDEEM_GIFT_ORDER_QUICK_ACTION, RESET_DEVICE_PIN_QUICK_ACTION, RESET_PASSWORD_QUICK_ACTION, RESUME_ORDER_QUICK_ACTION, REVIEW_STORE_ORDER_QUICK_ACTION, START_NEW_SUBSCRIPTION_ACTION, UNREVOKE_LOGIN_QUICK_ACTION, VALIDATE_PIN_QUICK_ACTION } from '../customerActionsTemplate/quick.actions';
import { STATE_OR_NAME as ADD_OFFER_STATE, REVIEW_SAVED_CART, REVIEW_SAVED_CART_INSTANCES, REVIEW_STORE_ORDER_ADD } from '../../reducers/constants/add.offer.wizard.constants';
import { STATE_OR_NAME as ADD_SERVICES_STATE } from '../../reducers/constants/add.services.wizard.constants';
import { RECONNECT_OFFER_STATE_NAME, STATE_OR_NAME as EDIT_OFFER_STATE, REVIEW_STORE_ORDER_EDIT } from '../../reducers/constants/edit.offer.wizard.constants';
import { MANAGE_SERVICE_FEATURES_ORDER_STATE_NAME as MANAGE_SERVICE_FEATURE_STATE, CREATE_PRODUCTS_ORDER_STATE_NAME as ADD_PRODUCT_STATE, CREATE_SUBSCRIPTIONS_ORDER_STATE_NAME as ADD_SUBSCRIPTION_STATE } from '../../reducers/constants/products.wizard.constants';
import { STORE_ORDER_TYPE } from '../shared/constants/customer.constants';
import { STATE_NAME as TRANSITION_OFFER_STATE, REVIEW_STORE_ORDER_TRANSITION } from './orders/transition/transition.offer.wizard.constants';
import { getNameByCountryCode } from './customer.helper';
import { setIsResumingStoreOrder, setIsResumingOrder } from '../../reducers/actions/add.offer.wizard.actions';
import { setIsResumingStoreOrderForEditOffer } from '../../reducers/actions/edit.offer.wizard.actions';
import { retrieveConvergentBillerSubscriberSummary, retrieveConvergentBillerAccountDetails } from '../../reducers/actions/customer.convergent.biller.actions';
import { retrieveCustomerAccountStatus } from '../../reducers/actions/customer.account.status.actions';
import { CustomerAccountStatusSelector } from '../../reducers/selectors/customer.account.status.selectors';
import { IsResumingStoreOrderSelector } from '../../reducers/selectors/transition.offer.wizard.selectors';
import { PROFILE_EVENTS } from './profile/profile.constants';
import { DISCONNECT_OFFERS_ITV_ROUTE } from './offers/disconnect/itv/disconnect.offers.itv.config';
import { DISCONNECT_CUSTOMER_DBSS_ROUTE } from './offers/disconnect/dbss/disconnect.offers.dbss.config';
import { BreachedAccountLevelUsageCapsMessageSelector, BreachedUsageCapsMessageSelector } from '../../reducers/selectors/usage.caps.selectors';
import { ShowServicesWidgetSelector } from '../../reducers/selectors/dashboard.selectors';
import { DASHBOARD_ROUTE } from '../../reducers/constants/dashboard.constants';
import { loadSubscriberRequirements } from '../../reducers/helpers/subscriber.requirements.actions.helper';
import { TRANSFER_STATE_NAME } from './orders/transfer/transfer.wizard.config';
import { CUSTOMER_CATEGORY } from '../createCustomerPopup/create.customer.popup.constants';
import { OrderingRouteConstants } from '@invision/customercare2';
import { NODE_TYPE } from '../../reducers/constants/customer.account.hierarchy.constants';
import { ACCOUNT_HIERARCHY_NODE_SERVICES_PAGE_REDIRECT } from '../../reactRoutes';
function CustomerCarePageController($ngRedux, $q, $scope, $state, $filter, $timeout, uiNotificationService) {
  var _this = this;
  var disconnectRedux;
  var mapStateToTarget = function mapStateToTarget(store) {
    return {
      breachedAccountLevelUsageCapsMessage: BreachedAccountLevelUsageCapsMessageSelector(store),
      breachedUsageCapsMessage: BreachedUsageCapsMessageSelector(store),
      canUserBypassPasswordReset: CanUserBypassResetSelector(store),
      caseTypes: CaseTypesSelector(store),
      casePriorityCodes: MetadataCodeTypeSelector(CODES.CasePriority, store),
      caseSeverityCodes: MetadataCodeTypeSelector(CODES.CaseSeverity, store),
      challengeAnswer: ChallengeAnswerSelector(store),
      convergentBillerAccountOfferings: ConvergentBillerAccountOfferingsSelector(store),
      currentAccountSummary: CurrentAccountSummarySelector(store),
      currentCustomer: CurrentCustomerSelector(store),
      currentCustomerId: CurrentCustomerIdSelector(store),
      currentCustomerName: CurrentCustomerNameSelector(store),
      currentCustomerAccountStatus: CustomerAccountStatusSelector(store),
      customerActionsTemplate: CustomerActionsTemplateSelector(store),
      creditLimitSummary: CreditLimitSummarySelector(store),
      formattedInvoices: FormattedInvoicesSelector(store),
      hierarchyName: HierarchyNameSelector(store),
      hasSubscriberPasswordAccess: HasSubscriberPasswordAccessSelector(store),
      instanceLevelOrdered: InstanceLevelOrderedSelector(store),
      invalidItemRsvp: InvalidInventoryItemSelector(store),
      isConsentsLoaded: MetadataCodeLoadedSelector(CODES.Consents, store),
      isCreditLimitBreached: CreditLimitBreachedSelector(store),
      isCustomerWrapUpLoaded: MetadataCodeLoadedSelector(CODES.CustomerWrapUp, store),
      isSubscriberSearchLoaded: MetadataCodeLoadedSelector(CODES.SubscriberSearchConfig, store),
      isApiResponseFaultCodeLoaded: MetadataCodeLoadedSelector(CODES.ApiResponseFaultCode, store),
      isDbss: IsDbss(store),
      isFetchingChallengeAnswer: IsFetchingChallengeAnswerSelector(store),
      isIpadOptimizedLayoutEnabled: IpadOptimizedLayoutFeatureToggleSelector(store),
      isFinalInvoiceGenerationOnRootInvoiceNodeEnabled: InvEnableFinalInvoiceGenerationOnRootInvoiceNodeFeatureToggleSelector(store),
      isPIIDataAccessEnabled: IsPIIDataAccessEnabledSelector(store),
      isProfilePageEnabled: IsProfilePageEnabledSelector(store),
      isResumingStoreOrder: IsResumingStoreOrderSelector(store),
      isFetchingCustomer: CustomerIsFetchingDataSelector(store),
      isWrappingCustomer: IsWrappingCustomerSelector(store),
      languages: MetadataCodeTypeSelector(CODES.Language, store),
      languagesLoaded: MetadataCodeLoadedSelector(CODES.Language, store),
      offeringId: OfferingIdSelector(store),
      offeringInstanceId: OfferingInstanceIdSelector(store),
      responsibleParty: ResponsiblePartyCustomerSelector(store),
      responsiblePartyName: ResponsiblePartyCustomerNameSelector(store),
      responsibilityAccountsItems: CurrentCustomerResponsibilityAccountsItemsSelector(store),
      routeCurrentState: RouteCurrentStateSelector(store),
      routeParams: RouteParams(store),
      selectedCustomerConsentsOptional: SelectedCustomerConsentsOptionalSelector(store),
      selectedCustomerUnacceptedConsents: SelectedCustomerConsentsMandatoryUnacceptedSelector(store),
      shoppingCart: ShoppingCartSelector(store),
      shoppingCartError: ShoppingCartErrorSelector(store),
      showAutoPayBanner: ShowAutoPayBannerSelector(store),
      showDetailedTaxationPerformanceBanner: ShowDetailedTaxationPerformanceBannerSelector(store),
      showServicesWidget: ShowServicesWidgetSelector(store),
      storeOrder: StoreOrderSelector(store),
      storeOrderError: StoreOrderErrorSelector(store),
      storeOrderType: StoreOrderTypeSelector(store),
      subscriberCategoryRequirements: SubscriberCategoryRequirementsForCurrentBusinessUnitOrDefault(store),
      subscriberRequirements: SubscriberRequirementsForCurrentBusinessUnitOrDefault(store),
      userSecurityAttributes: UserSecurityAttributesSelector(store),
      warningTranslatedMessage: ARMWarningTranslatedMessageSelector(store)
    };
  };
  var visibleNavBlocklist = [ADD_OFFER_STATE, ADD_PRODUCT_STATE, ADD_SERVICES_STATE, ADD_SUBSCRIPTION_STATE, EDIT_OFFER_STATE, REVIEW_STORE_ORDER_EDIT, MANAGE_SERVICE_FEATURE_STATE, OrderingRouteConstants.ADD_PLAN_ORDER_PAGE.name, OrderingRouteConstants.EDIT_SERVICE_ATTRIBUTES_PAGE.name, OrderingRouteConstants.REMOVE_PLAN_ORDER_PAGE.name, OrderingRouteConstants.REMOVE_SERVICES_ORDER_PAGE.name, OrderingRouteConstants.SUSPEND_RESUME_SERVICES_ORDER_PAGE.name, OrderingRouteConstants.SWAP_PLAN_ORDER_PAGE.name, OrderingRouteConstants.UPDATE_CHARGE_AMOUNT_PAGE.name, OrderingRouteConstants.UPDATE_OFFERING_ADDITIONAL_PROPERTIES_PAGE.name, RECONNECT_OFFER_STATE_NAME, TRANSFER_STATE_NAME, TRANSITION_OFFER_STATE, REVIEW_STORE_ORDER_TRANSITION, 'index.customercare.customer.subscriptions.detail.replace', 'index.customercare.customer.subscriptions.detail.restore'];
  this.$onInit = function () {
    var controllerActions = {
      cancelAnonymizeCustomer: cancelAnonymizeCustomer,
      cancelStoreOrder: cancelStoreOrder,
      clearSavedShoppingCart: clearSavedShoppingCart,
      customerResetDevicePin: customerResetDevicePin,
      customerResetPassword: customerResetPassword,
      customerResetRevokedPassword: customerResetRevokedPassword,
      customerWrapUp: customerWrapUp,
      fetchCodeType: fetchCodeTypes,
      fetchShoppingCart: fetchShoppingCart,
      fetchStoreOrder: fetchStoreOrder,
      getConsents: fetchConsents,
      loadSubscriberRequirements: loadSubscriberRequirements,
      retrieveCustomer: retrieveCustomer,
      resetCustomerRecoverable: resetCustomerRecoverable,
      retrieveConvergentBillerSubscriberSummary: retrieveConvergentBillerSubscriberSummary,
      retrieveConvergentBillerAccountDetails: retrieveConvergentBillerAccountDetails,
      retrieveCustomerAccountStatus: retrieveCustomerAccountStatus,
      retrieveCustomerChallengeAnswer: retrieveCustomerChallengeAnswer,
      retrieveDevicePinStatus: retrieveDevicePinStatus,
      retrieveHierarchy: retrieveHierarchy,
      searchHierarchyNodes: searchHierarchyNodes,
      setIsResumingOrder: setIsResumingOrder,
      setIsResumingStoreOrder: setIsResumingStoreOrder,
      setNavGroup: setNavGroup,
      stateGo: stateGo,
      stateReload: stateReload,
      setIsEditingProfile: setIsEditingProfile,
      setIsResumingStoreOrderForEditOffer: setIsResumingStoreOrderForEditOffer
    };
    disconnectRedux = $ngRedux.connect(mapStateToTarget, controllerActions)(function (state, actions) {
      _this.state = state;
      _this.actions = actions;
    });
    _this.CustomerCareLocaleKeys = CustomerCareLocaleKeys;
    _this.CoreLocaleKeys = CoreLocaleKeys;
    _this.anonymizationNotificationExpanded = false;
    _this.anonymizationExpanderLabel = i18n.translate(CustomerCareLocaleKeys.ANONYMIZATION.EXPAND_LABEL);
    _this.resetPassword = true;
    _this.visibleQuickActions = VISIBLE_QUICK_ACTIONS;
    _this.showCustomerWrapUp = false;
    _this.isStoreOrderError = false;
    _this.immediateAnonymizationHour = 1;
    _this.isCreateChildNodeModalOpen = false;
    _this.isChangeBillingTypeModalOpen = false;
    _this.actions.loadSubscriberRequirements().then(function () {
      _this.requirements = _this.state.currentCustomer.Category === CUSTOMER_CATEGORY.RESIDENTIAL ? _this.state.subscriberRequirements : _this.state.subscriberCategoryRequirements;
      _this.isDataLoaded = true;
    });
    if (_this.state.currentCustomer.ScheduledAnonymization) {
      var anonymizationMoment = moment(_this.state.currentCustomer.ScheduledAnonymization, 'YYYY-MM-DDTHH:mm:ss.SSSZ');
      var anonymizationDate = $filter('localShort')(anonymizationMoment);
      var timeDiff = moment.duration(moment(anonymizationDate).diff(moment(new Date()))).asHours();
      //checking the time difference in the Scheduled Anonymization to differentiate Schedule Anonymization and Immediate Anonymization
      if (timeDiff && timeDiff < _this.immediateAnonymizationHour) {
        _this.anonymizationMessage = i18n.translate(CustomerCareLocaleKeys.ANONYMIZATION.IMMEDIATE_MESSAGE);
        _this.expandedAnonymizationMessage = i18n.translate(CustomerCareLocaleKeys.ANONYMIZATION.IMMEDIATE_EXPANDED_MESSAGE);
      } else {
        _this.anonymizationMessage = i18n.translate(CustomerCareLocaleKeys.ANONYMIZATION.MESSAGE, {
          anonymizationDate: anonymizationDate
        });
        _this.expandedAnonymizationMessage = i18n.translate(CustomerCareLocaleKeys.ANONYMIZATION.EXPANDED_MESSAGE, {
          anonymizationDate: anonymizationDate
        });
      }
    }
    if (_this.state.currentCustomer.ConvergentBillerId && !_this.state.isFetchingAccountSummary) {
      _this.actions.retrieveConvergentBillerSubscriberSummary(_this.state.currentCustomerId, true);
    }
    if (!_this.state.isConsentsLoaded) {
      _this.actions.getConsents();
    }
    if (!_this.state.isSubscriberSearchLoaded) {
      _this.actions.fetchCodeType(CODES.SubscriberSearchConfig);
    }
    if (!_this.state.isApiResponseFaultCodeLoaded) {
      _this.actions.fetchCodeType(CODES.ApiResponseFaultCode);
    }
    _this.fetchShoppingCart();
    if (_this.state.isDbss) {
      var _this$state$convergen;
      _this.fetchStoreOrder();
      if (!((_this$state$convergen = _this.state.convergentBillerAccountOfferings) !== null && _this$state$convergen !== void 0 && _this$state$convergen.length)) {
        _this.actions.retrieveConvergentBillerAccountDetails(_this.state.currentCustomerId).catch(function (error) {
          uiNotificationService.transientError(error.translatedMessage);
        });
      }
    }
    _this.identifierAbbreviationLabel = i18n.translate(CustomerCareLocaleKeys.IDENTIFIER_ABBREVIATION);
    _this.quickInfoPopup = {
      onRegisterApi: function onRegisterApi(evt) {
        _this.quickInfoPopupApi = evt.api;
      }
    };
    _this.openQuickInfoPopup = function () {
      _this.actions.retrieveCustomerAccountStatus(_this.state.currentCustomer.Id).then(function () {
        _this.quickInfoPopupApi.open();
      });
    };
    _this.closeQuickInfoPopup = function () {
      _this.quickInfoPopupApi.close();
    };
    _this.showDiscountsModal = false;
    _this.discretionaryDiscountsConfig = {
      onRegisterApi: function onRegisterApi(_ref) {
        var api = _ref.api;
        _this.discretionaryDiscountsPopupApi = api;
      }
    };
    _this.openDiscretionaryDiscountsPopup = function () {
      _this.showDiscountsModal = true;
      _this.discretionaryDiscountsPopupApi.open();
    };
    _this.closeDiscretionaryDiscountsPopup = function () {
      _this.showDiscountsModal = false;
      _this.discretionaryDiscountsPopupApi.close();
    };
    _this.retrieveCustomerChallengeAnswer = function () {
      _this.actions.retrieveCustomerChallengeAnswer(_this.state.currentCustomer.Id);
    };
    _this.caseModalProps = {
      isOpen: false,
      onClose: function onClose() {
        _this.caseModalProps.isOpen = false;
      },
      onSubmit: function onSubmit(caseDetails) {
        _this.caseModalProps.isOpen = false;
        uiNotificationService.success(i18n.translate(CustomerCareLocaleKeys.CASE.CREATED_SUCCESSFULLY));
        _this.actions.stateGo(CASES_DETAILS_STATE_REDIRECT, {
          customerId: _this.state.currentCustomerId,
          caseId: caseDetails.Id.Value
        });
      },
      openCaseModal: function openCaseModal() {
        _this.caseModalProps.isOpen = true;
      },
      routeParams: {
        customerId: _this.state.currentCustomerId
      }
    };
    if (_this.state.currentCustomer.HierarchyId) {
      _this.actions.retrieveHierarchy(_this.state.currentCustomer.HierarchyId).then(function () {
        if (_this.state.currentCustomer.HierarchyParentSubscriberId) {
          _this.actions.searchHierarchyNodes(_this.state.currentCustomer.Id, {
            NodeTypes: [NODE_TYPE.REGULAR, NODE_TYPE.INHERITED],
            ParentSubscriberId: _this.state.currentCustomer.HierarchyParentSubscriberId
          });
        }
      });
    }
    if (!_this.state.languagesLoaded) {
      _this.actions.fetchCodeType(CODES.Language);
    }
    _this.cancelAnonymizationPopupConfig = {
      onRegisterApi: function onRegisterApi(event) {
        _this.cancelAnonymizationPopupApi = event.api;
      }
    };
    _this.handleResultCancelAnonymizationPopup = function (proceed) {
      if (proceed) {
        _this.cancelAnonymization();
      } else {
        _this.handleCloseAnonymizationPopup();
      }
    };
    _this.handleCloseAnonymizationPopup = function () {
      _this.cancelAnonymizationPopupApi.close();
    };
    _this.cancelAnonymizationPopupConfig = {
      onRegisterApi: function onRegisterApi(event) {
        _this.cancelAnonymizationPopupApi = event.api;
      }
    };
    _this.handleResultCancelAnonymizationPopup = function (proceed) {
      if (proceed) {
        _this.cancelAnonymization();
      } else {
        _this.handleCloseAnonymizationPopup();
      }
    };
    _this.handleCloseAnonymizationPopup = function () {
      _this.cancelAnonymizationPopupApi.close();
    };
    _this.customerWrapUpPopup = {
      onRegisterApi: function onRegisterApi(_ref2) {
        var api = _ref2.api;
        _this.customerWrapUpPopupApi = api;
      }
    };
    _this.cancelStoreOrderConfirmationPopupConfig = {
      onRegisterApi: function onRegisterApi(_ref3) {
        var api = _ref3.api;
        _this.cancelStoreOrderConfirmationPopupApi = api;
      }
    };
    _this.clearSavedCartConfirmationPopupConfig = {
      onRegisterApi: function onRegisterApi(_ref4) {
        var api = _ref4.api;
        _this.clearSavedCartConfirmationPopupApi = api;
      }
    };
    _this.customerPinPopupConfig = {
      onRegisterApi: function onRegisterApi(_ref5) {
        var api = _ref5.api;
        _this.customerPinPopupApi = api;
      }
    };
    _this.handleAngularToReactActionLoads(_this.state.routeParams);
  };
  this.openCustomerPinPopup = function () {
    _this.showCustomerPinPopup = true;
    $timeout(function () {
      _this.customerPinPopupApi.open();
    });
  };
  this.closeCustomerPinPopup = function () {
    _this.showCustomerPinPopup = false;
    _this.customerPinPopupApi.close();
  };
  this.$onDestroy = function () {
    disconnectRedux();
    _this.quickInfoPopupApi.close();
    _this.actions.resetCustomerRecoverable();
  };
  this.showAutoPayBanner = function () {
    return _this.state.showAutoPayBanner && _this.state.currentAccountSummary && _this.state.currentAccountSummary.AccountNumber && _this.state.currentAccountSummary.AutoPayConfigured;
  };
  this.showBreachedUsageCapsBanner = function () {
    return _this.state.routeCurrentState.name === DASHBOARD_ROUTE && _this.state.showServicesWidget && _this.state.breachedUsageCapsMessage.length;
  };
  this.showBreachedUsageAccountCapsBanner = function () {
    return _this.state.routeCurrentState.name === DASHBOARD_ROUTE && _this.state.showServicesWidget && _this.state.breachedAccountLevelUsageCapsMessage.length;
  };
  this.showBreachedCreditCheckBanner = function () {
    if (_this.state.creditLimitSummary) {
      if (_this.state.creditLimitSummary.CreditLimitBreached && _this.state.creditLimitSummary.RemainingCreditAmount < 0) {
        _this.creditLimitBreachErrorMessage = i18n.translate(CustomerCareLocaleKeys.CREDIT_LIMIT_BREACHED, {
          breachAmount: $filter('invCurrency')(_this.state.creditLimitSummary.RemainingCreditAmount, _this.state.currentCustomer.SubscriberCurrency)
        });
      } else if (_this.state.creditLimitSummary.CreditLimitBreached && !(_this.state.creditLimitSummary.RemainingCreditAmount < 0)) {
        _this.creditLimitBreachErrorMessage = i18n.translate(CustomerCareLocaleKeys.CREDIT_LIMIT_BREACHED_NO_AMOUNT);
      }
    }
    return _this.state.routeCurrentState.name === DASHBOARD_ROUTE && !isEmpty(_this.state.creditLimitSummary) && _this.state.isCreditLimitBreached && _this.state.currentCustomer.SubscriberCurrency;
  };
  this.showBillCycleChangeInProgressBanner = function () {
    var _this$state$currentCu;
    if ((_this$state$currentCu = _this.state.currentCustomer.SubscriberTypeDetails) !== null && _this$state$currentCu !== void 0 && _this$state$currentCu.BillCycleChangeInProgress) {
      _this.billCycleChangeInProgressWarningMessage = i18n.translate(CustomerCareLocaleKeys.PROFILE_PAGE.BILL_CYCLE_CHANGE_IN_PROGRESS_WARNING_MESSAGE);
      return true;
    }
    return false;
  };
  this.showFinalInvoiceInProgressBanner = function () {
    var _this$state$currentCu2;
    if (_this.state.isFinalInvoiceGenerationOnRootInvoiceNodeEnabled && (_this$state$currentCu2 = _this.state.currentCustomer.SubscriberTypeDetails) !== null && _this$state$currentCu2 !== void 0 && _this$state$currentCu2.FinalInvoiceInProgress) {
      _this.finalInvoiceInProgressWarningMessage = i18n.translate(CustomerCareLocaleKeys.PROFILE_PAGE.FINAL_INVOICE_IN_PROGRESS_WARNING_MESSAGE);
      return true;
    }
    return false;
  };
  this.showHierarchyMoveInProgressBanner = function () {
    var _this$state$currentCu3;
    if (((_this$state$currentCu3 = _this.state.currentCustomer.SubscriberTypeDetails) === null || _this$state$currentCu3 === void 0 ? void 0 : _this$state$currentCu3.HierarchyMoveStatus) === HIERARCHY_MOVE_STATUS_TYPES.AcrossHierarchies) {
      _this.hierarchyMoveInProgressWarningMessage = i18n.translate(CustomerCareLocaleKeys.PROFILE_PAGE.HIERARCHY_MOVE_IN_PROGRESS_WARNING_MESSAGE);
      return true;
    }
    return false;
  };
  this.toggleAnonymizationMessage = function () {
    _this.anonymizationNotificationExpanded = !_this.anonymizationNotificationExpanded;
    _this.anonymizationExpanderLabel = _this.anonymizationNotificationExpanded ? i18n.translate(CustomerCareLocaleKeys.ANONYMIZATION.COLLAPSE_LABEL) : i18n.translate(CustomerCareLocaleKeys.ANONYMIZATION.EXPAND_LABEL);
  };
  this.handleToggleNavigatorCollapsedSelected = function () {
    _this.actions.toggleCollapsedSelected();
  };
  this.canUserCancelAnonymization = function () {
    return hasAdminAccess(_this.state.userSecurityAttributes, SUBSCRIBER_ADMIN_ACCESS);
  };
  this.showCancelAnonymization = function () {
    _this.cancelAnonymizationPopupApi.open();
  };
  this.openClearSavedCartConfirmationPopup = function () {
    _this.clearSavedCartConfirmationPopupApi.open();
  };
  this.closeClearSavedCartConfirmationPopup = function () {
    _this.clearSavedCartConfirmationPopupApi.close();
  };
  this.openCancelStoreOrderConfirmationPopup = function () {
    _this.cancelStoreOrderConfirmationPopupApi.open();
  };
  this.closeCancelStoreOrderConfirmationPopup = function () {
    _this.isStoreOrderError = false;
    _this.cancelStoreOrderConfirmationPopupApi.close();
  };
  this.cancelStoreOrder = function (confirm) {
    if (confirm) {
      _this.actions.cancelStoreOrder(_this.state.currentCustomerId).then(function () {
        if (_this.state.isResumingStoreOrder) {
          _this.actions.setIsResumingStoreOrder(false);
        }
        uiNotificationService.transientSuccess(i18n.translate(CustomerCareLocaleKeys.CANCEL_STORE_ORDER_CONFIRMATION_POPUP.SUCCESS_MESSAGE));
      }).catch(function (error) {
        uiNotificationService.transientError(error.translatedMessage);
      });
    }
    _this.closeCancelStoreOrderConfirmationPopup();
  };
  this.clearSavedCart = function (confirm) {
    if (confirm) {
      _this.actions.clearSavedShoppingCart(_this.state.currentCustomerId).then(function () {
        uiNotificationService.transientSuccess(i18n.translate(CustomerCareLocaleKeys.CLEAR_SAVED_CART_CONFIRMATION_POPUP.SUCCESS_MESSAGE));
      }).catch(function (error) {
        uiNotificationService.transientError(error.translatedMessage);
      }).finally(function () {
        _this.closeClearSavedCartConfirmationPopup();
      });
    } else {
      _this.closeClearSavedCartConfirmationPopup();
    }
  };
  this.cancelAnonymization = function () {
    _this.handleCloseAnonymizationPopup();
    _this.actions.cancelAnonymizeCustomer(_this.state.currentCustomer.Id).then(function () {
      uiNotificationService.success(i18n.translate(CustomerCareLocaleKeys.ANONYMIZATION.CANCEL_ANONYMIZATION_SUCCESS), null, {
        timeOut: NOTIFICATION_TIME_LENGTH
      });
      _this.actions.stateReload();
    }).catch(function () {
      uiNotificationService.error(i18n.translate(CustomerCareLocaleKeys.ANONYMIZATION.CANCEL_ANONYMIZATION_FAILURE), i18n.translate(CustomerCareLocaleKeys.ANONYMIZATION.CANCEL_ANONYMIZATION_FAILURE_TITLE), {
        timeOut: NOTIFICATION_TIME_LENGTH
      });
    });
  };
  this.onQuickActionSelected = function (_ref6) {
    var id = _ref6.id,
      isLocked = _ref6.isLocked;
    if (!isLocked) {
      switch (id) {
        case CANCEL_STORE_ORDER_QUICK_ACTION.id:
          {
            _this.openCancelStoreOrderConfirmationPopup();
            break;
          }
        case CLEAR_SAVED_CART_QUICK_ACTION.id:
          {
            _this.openClearSavedCartConfirmationPopup();
            break;
          }
        case NEW_DBSS_ORDER_QUICK_ACTION.id:
          {
            _this.actions.stateGo(ADD_OFFER_STATE, {
              customerId: _this.state.currentCustomerId
            });
            break;
          }
        case NEW_PRODUCT_ORDER_QUICK_ACTION.id:
          {
            _this.actions.stateGo(ADD_PRODUCT_STATE, {
              productsOnly: true
            });
            break;
          }
        case OPEN_CASE_QUICK_ACTION.id:
          {
            _this.caseModalProps.openCaseModal();
            break;
          }
        case REDEEM_GIFT_ORDER_QUICK_ACTION.id:
          {
            _this.actions.stateGo('index.customercare.customer.redeemGiftOrder');
            break;
          }
        case RESET_DEVICE_PIN_QUICK_ACTION.id:
          {
            _this.openResetDevicePinDialog();
            break;
          }
        case RESET_PASSWORD_QUICK_ACTION.id:
          {
            _this.resetRevokedPasswordPopUp.open();
            break;
          }
        case UNREVOKE_LOGIN_QUICK_ACTION.id:
          {
            _this.resetRevokedLoginPopUp.open();
            break;
          }
        case DISCONNECT_CUSTOMER_QUICK_ACTION.id:
          {
            if (_this.state.isDbss) {
              _this.actions.stateGo(DISCONNECT_CUSTOMER_DBSS_ROUTE);
            } else {
              _this.actions.stateGo(DISCONNECT_OFFERS_ITV_ROUTE);
            }
            break;
          }
        case RESUME_ORDER_QUICK_ACTION.id:
          {
            _this.resumeOrder();
            break;
          }
        case REVIEW_STORE_ORDER_QUICK_ACTION.id:
          {
            if (_this.state.storeOrderError.code === INVALID_SHOPPING_CART_FAULT_CODE) {
              _this.isStoreOrderError = true;
              _this.openCancelStoreOrderConfirmationPopup();
            } else if (_this.state.storeOrderType === STORE_ORDER_TYPE.ADD) {
              _this.actions.setIsResumingStoreOrder(true);
              _this.actions.stateGo(REVIEW_STORE_ORDER_ADD, {
                customerId: _this.state.currentCustomerId
              });
            } else if (_this.state.storeOrderType === STORE_ORDER_TYPE.MODIFY) {
              _this.actions.setIsResumingStoreOrderForEditOffer(true);
              _this.actions.stateGo(REVIEW_STORE_ORDER_EDIT, {
                customerId: _this.state.currentCustomerId,
                offeringId: _this.state.offeringId,
                offeringInstanceId: _this.state.offeringInstanceId
              });
            } else if (_this.state.storeOrderType === STORE_ORDER_TYPE.TRANSITION) {
              _this.actions.setIsResumingStoreOrder(true);
              _this.actions.stateGo(REVIEW_STORE_ORDER_TRANSITION, {
                offeringId: _this.state.storeOrder.SourceTransitionOfferingId,
                offeringInstanceId: _this.state.storeOrder.SourceTransitionOfferingInstanceId
              });
            }
            break;
          }
        case START_NEW_SUBSCRIPTION_ACTION.id:
          {
            _this.actions.stateGo(ADD_SUBSCRIPTION_STATE);
            break;
          }
        case CUSTOMER_WRAP_UP_QUICK_ACTION.id:
          {
            _this.openCustomerWrapUpPopup();
            break;
          }
        case VALIDATE_PIN_QUICK_ACTION.id:
          {
            _this.openCustomerPinPopup();
            break;
          }
        case CHANGE_BILLING_TYPE_QUICK_ACTION.id:
          {
            _this.isChangeBillingTypeModalOpen = true;
            break;
          }
        case MANAGE_SERVICES_QUICK_ACTION.id:
          {
            var _this$state$currentCu4;
            _this.actions.stateGo(ACCOUNT_HIERARCHY_NODE_SERVICES_PAGE_REDIRECT, {
              customerId: _this.state.currentCustomerId,
              hierarchyId: (_this$state$currentCu4 = _this.state.currentCustomer) === null || _this$state$currentCu4 === void 0 ? void 0 : _this$state$currentCu4.HierarchyId,
              hierarchyNodeSubscriberId: _this.state.currentCustomerId
            });
            break;
          }
        case CREATE_COMMERCIAL_CHILD_QUICK_ACTION.id:
          {
            _this.isCreateChildNodeModalOpen = true;
            break;
          }
        default:
          break;
      }
    }
  };
  this.onCloseCreateChildNodeModal = function (nodeCreated) {
    if (nodeCreated) {
      uiNotificationService.success(i18n.translate(CustomerCareLocaleKeys.CREATE_COMMERCIAL_CHILD_SUCCESS));
    }
    _this.isCreateChildNodeModalOpen = false;
  };
  this.onCreateChildNodeModalError = function (errorMessage) {
    uiNotificationService.error(errorMessage);
  };
  this.onCloseChangeBillingTypeModal = function (nodeCreated) {
    if (nodeCreated) {
      uiNotificationService.success(i18n.translate(CustomerCareLocaleKeys.ACCOUNT_HIERARCHY.CHANGE_BILLING_TYPE.UPDATE_SUCCESS_TEXT));
    }
    _this.isChangeBillingTypeModalOpen = false;
  };
  this.onChangeBillingTypeModalError = function (errorMessage) {
    uiNotificationService.error(errorMessage);
  };

  /* To be cleaned up later, once we have ordering flows ported to React. */
  this.handleAngularToReactActionLoads = function (params) {
    var ReactToAngularActions = {
      ReviewSavedCart: 'reviewSavedCart',
      ReviewStoreOrder: 'reviewStoreOrder'
    };
    if (params.action) {
      switch (params.action) {
        case ReactToAngularActions.ReviewSavedCart:
          _this.resumeOrder();
        case ReactToAngularActions.ReviewStoreOrder:
          _this.reviewStoreOrder(+params.storeOrderType, params.offeringId, params.offeringInstanceId);
        default:
          return;
      }
    }
  };
  this.resumeOrder = function () {
    if (_this.state.shoppingCartError !== null && _this.state.invalidItemRsvp === undefined) {
      _this.actions.setIsResumingOrder(true);
      _this.resumeCartErrorPopup.open();
    } else if (_this.state.invalidItemRsvp && _this.state.invalidItemRsvp.length > 0) {
      _this.invalidInventoryErrorPopUpConfig.open();
    } else {
      _this.actions.setIsResumingOrder(true);
      _this.actions.stateGo(_this.state.instanceLevelOrdered ? REVIEW_SAVED_CART_INSTANCES : REVIEW_SAVED_CART, {
        customerId: _this.state.currentCustomerId
      });
    }
  };
  this.reviewStoreOrder = function (orderType, offeringId, offeringInstanceId) {
    if (orderType === STORE_ORDER_TYPE.ADD) {
      _this.actions.setIsResumingStoreOrder(true);
      _this.actions.stateGo(REVIEW_STORE_ORDER_ADD, {
        customerId: _this.state.currentCustomerId
      });
    } else if (orderType === STORE_ORDER_TYPE.MODIFY) {
      _this.actions.setIsResumingStoreOrderForEditOffer(true);
      _this.actions.stateGo(REVIEW_STORE_ORDER_EDIT, {
        customerId: _this.state.currentCustomerId,
        offeringId: _this.state.offeringId || offeringId,
        offeringInstanceId: _this.state.offeringInstanceId || offeringInstanceId
      });
    } else if (orderType === STORE_ORDER_TYPE.TRANSITION) {
      _this.actions.setIsResumingStoreOrder(true);
      _this.fetchStoreOrder().then(function () {
        _this.actions.stateGo(REVIEW_STORE_ORDER_TRANSITION, {
          offeringId: _this.state.storeOrder.SourceTransitionOfferingId || offeringId,
          offeringInstanceId: _this.state.storeOrder.SourceTransitionOfferingInstanceId || offeringInstanceId
        });
      });
    } else {
      _this.isStoreOrderError = true;
      _this.openCancelStoreOrderConfirmationPopup();
    }
  };
  this.fetchShoppingCart = function () {
    return _this.actions.fetchShoppingCart(_this.state.currentCustomer.Id);
  };
  this.fetchStoreOrder = function () {
    return _this.actions.fetchStoreOrder(_this.state.currentCustomer.Id);
  };
  this.fetchDevicePinStatus = function () {
    return _this.actions.retrieveDevicePinStatus(_this.state.currentCustomer.Id);
  };
  this.isNavVisible = function () {
    return _this.state && (!_this.state.routeCurrentState || !visibleNavBlocklist.includes(_this.state.routeCurrentState.name));
  };
  this.resetPasswordPopUp = {
    onRegisterApi: function onRegisterApi(evt) {
      _this.resetPasswordPopUp = evt.api;
    }
  };
  this.resetRevokedPasswordPopUp = {
    onRegisterApi: function onRegisterApi(evt) {
      _this.resetRevokedPasswordPopUp = evt.api;
    }
  };
  this.resetRevokedLoginPopUp = {
    onRegisterApi: function onRegisterApi(evt) {
      _this.resetRevokedLoginPopUp = evt.api;
    }
  };
  this.openResetPasswordDialog = function () {
    _this.resetPasswordPopUp.open();
  };
  this.openResetRevokedPasswordDialog = function () {
    if (!_this.state.canUserBypassPasswordReset) {
      _this.resetRevokedPasswordPopUp.open();
    } else {
      _this.resetRevokedLoginPopUp.open();
    }
  };
  this.closeResetPasswordDialog = function () {
    _this.resetPasswordPopUp.close();
  };
  this.closeResetRevokedPasswordDialog = function () {
    _this.resetRevokedPasswordPopUp.close();
  };
  this.closeResetRevokedLoginDialog = function () {
    _this.resetRevokedLoginPopUp.close();
  };
  this.resetDevicePinPopUp = {
    onRegisterApi: function onRegisterApi(evt) {
      _this.resetDevicePinPopUp = evt.api;
    }
  };
  this.openResetDevicePinDialog = function () {
    _this.resetDevicePinPopUp.open();
  };
  this.closeResetDevicePinDialog = function () {
    _this.resetDevicePinPopUp.close();
  };
  this.invalidInventoryErrorPopUpConfig = {
    onRegisterApi: function onRegisterApi(evt) {
      _this.invalidInventoryErrorPopUpConfig = evt.api;
    }
  };
  this.resumeCartErrorPopup = {
    onRegisterApi: function onRegisterApi(evt) {
      _this.resumeCartErrorPopup = evt.api;
    }
  };
  this.getReasonCode = function (code) {
    switch (code) {
      case INVALID_INVENTORY_REASON_CODES.NOT_AVAILABLE:
        return CustomerCareLocaleKeys.RESUME_SAVED_ORDER_FAILURE.NOT_AVAILABLE;
      case INVALID_INVENTORY_REASON_CODES.NOT_FOUND:
        return CustomerCareLocaleKeys.RESUME_SAVED_ORDER_FAILURE.NOT_FOUND;
      case INVALID_INVENTORY_REASON_CODES.NOT_VALID:
        return CustomerCareLocaleKeys.RESUME_SAVED_ORDER_FAILURE.NOT_VALID;
      default:
        return;
    }
  };
  this.getFriendlyName = function (countryCode) {
    return getNameByCountryCode(_this.state.languages, countryCode);
  };
  this.continueToDecisionOffer = function () {
    _this.invalidInventoryErrorPopUpConfig.close();
    _this.actions.setIsResumingOrder(true);
    _this.actions.stateGo(ADD_OFFER_STATE, {
      customerId: _this.state.currentCustomerId
    });
  };
  this.continueToAddOffer = function () {
    _this.resumeCartErrorPopup.close();
    _this.actions.stateGo(ADD_OFFER_STATE, {
      customerId: _this.state.currentCustomerId
    });
  };
  this.onGroupNavSelected = function (navItem) {
    _this.actions.setNavGroup(navItem);
  };
  this.resetSubscriberPassword = function (result) {
    // Double-check the permissions to avoid any scripting hacks
    if (result && hasAccess(_this.state.userSecurityAttributes, RESET_SUBSCRIBER_PASSWORD_ACCESS)) {
      if (_this.state.currentCustomer.LoginRevoked) {
        _this.resetRevokedPassword();
      } else {
        _this.actions.customerResetPassword(_this.state.currentCustomer.Login).then(function () {
          uiNotificationService.success(i18n.translate(CustomerCareLocaleKeys.RESET_PASSWORD_SUCCESS), null, {
            timeOut: NOTIFICATION_TIME_LENGTH
          });
        }).catch(function () {
          uiNotificationService.error(i18n.translate(CustomerCareLocaleKeys.RESET_PASSWORD_FAILURE), null, {
            timeOut: NOTIFICATION_TIME_LENGTH
          });
        });
      }
    }
    _this.resetPasswordPopUp.close();
  };
  this.resetRevokedSubscriberPassword = function (result) {
    // Double-check the permissions to avoid any scripting hacks
    if (result && hasAccess(_this.state.userSecurityAttributes, RESET_SUBSCRIBER_PASSWORD_ACCESS)) {
      _this.resetRevokedPassword(_this.state.canUserBypassPasswordReset && !_this.resetPassword);
    }
    _this.closeResetRevokedPasswordDialog();
    _this.closeResetRevokedLoginDialog();
  };
  this.resetSubscriberDevicePin = function (result) {
    if (result && hasAccess(_this.state.userSecurityAttributes, SUBSCRIBER_ADMIN_ACCESS)) {
      _this.actions.customerResetDevicePin(_this.state.currentCustomer.Id).then(function () {
        uiNotificationService.success(i18n.translate(CustomerCareLocaleKeys.RESET_DEVICE_PIN_SUCCESS), null, {
          timeOut: NOTIFICATION_TIME_LENGTH
        });
        return _this.fetchDevicePinStatus();
      }).then(_this.actions.retrieveCustomerAccountStatus).catch(function () {
        uiNotificationService.error(i18n.translate(CustomerCareLocaleKeys.RESET_DEVICE_PIN_FAILURE), null, {
          timeOut: NOTIFICATION_TIME_LENGTH
        });
      });
    }
    _this.resetDevicePinPopUp.close();
  };
  this.handleClickResponsiblePartyButton = function () {
    _this.actions.stateGo('index.customercare.customer', {
      customerId: _this.state.responsibleParty.Id
    });
  };
  this.handleSelectResponsibilityAccount = function (responsibilityAccount) {
    _this.actions.stateGo('index.customercare.customer', {
      customerId: responsibilityAccount.customerId
    });
  };
  this.shouldUseFirstAndLastName = function () {
    return _this.state.currentCustomer.FirstName && _this.state.currentCustomer.LastName;
  };
  this.primaryIdentifierIsExternalRef = function () {
    return !_this.shouldUseFirstAndLastName() && _this.state.currentCustomer.ExternalReference && _this.state.currentCustomer.ExternalReference === _this.state.currentCustomerName;
  };
  this.primaryIdentifierIsEmail = function () {
    return !_this.shouldUseFirstAndLastName() && _this.state.currentCustomer.Email && _this.state.currentCustomer.Email === _this.state.currentCustomerName;
  };
  this.resetRevokedPassword = function (bypassResetPassword) {
    _this.actions.customerResetRevokedPassword(_this.state.currentCustomer.Id, bypassResetPassword).then(function () {
      uiNotificationService.success(i18n.translate(bypassResetPassword ? CustomerCareLocaleKeys.RESET_REVOKE_SUCCESS : CustomerCareLocaleKeys.RESET_PASSWORD_SUCCESS), null, {
        timeOut: NOTIFICATION_TIME_LENGTH
      });
      return _this.actions.retrieveCustomer(_this.state.currentCustomer.Id);
    }).then(function () {
      return _this.actions.retrieveCustomerAccountStatus(_this.state.currentCustomer.Id);
    }).catch(function () {
      uiNotificationService.error(i18n.translate(bypassResetPassword ? CustomerCareLocaleKeys.RESET_REVOKE_FAILURE : CustomerCareLocaleKeys.RESET_PASSWORD_FAILURE), null, {
        timeOut: NOTIFICATION_TIME_LENGTH
      });
    });
  };
  this.isVisibleMandatoryConsentNotification = function () {
    return _this.state && _this.state.selectedCustomerUnacceptedConsents.length > 0;
  };
  this.openEditCustomerModal = function () {
    var profilePageStateName = 'index.customercare.customer.profile';
    if (profilePageStateName === $state.current.name) {
      $scope.$broadcast(PROFILE_EVENTS.OPEN_EDIT_CUSTOMER_MODAL);
    } else {
      $state.go(profilePageStateName, {
        'customerId': _this.state.currentCustomerId,
        'openEditCustomerModal': true
      });
    }
  };
  this.openCustomerWrapUpPopup = function () {
    _this.showCustomerWrapUp = true;
    $timeout(_this.customerWrapUpPopupApi.open);
  };
  this.closeCustomerWrapupModal = function () {
    _this.showCustomerWrapUp = false;
    _this.customerWrapUpPopupApi.close();
  };
  this.onCustomerWrapUp = function () {
    _this.isWrapupValid && _this.actions.customerWrapUp({
      customerId: _this.state.currentCustomerId,
      reasonId: _this.wrapupReason,
      comments: _this.wrapupComments
    }).then(function () {
      _this.closeCustomerWrapupModal();
      _this.actions.stateGo(ROOT_CARE_MODULE_ROUTE);
    }).catch(function (error) {
      _this.uiNotificationService.transientError(error.translatedMessage);
    });
  };
  this.onWrapupUpdate = function (formModel) {
    var wrapupReasonCode = pathOr(undefined, ['reason', 'Value'], formModel);
    _this.isWrapupValid = !!wrapupReasonCode;
    _this.wrapupReason = wrapupReasonCode;
    _this.wrapupComments = formModel.comments;
  };
  this.closePinValidationModal = function (result) {
    _this.closeCustomerPinPopup();
    if (result) {
      uiNotificationService.transientSuccess(i18n.translate(CustomerCareLocaleKeys.PROFILE_PAGE.PIN_VALIDATION_SUCCESS));
    }
  };
}
export default {
  template: require('./customer.html'),
  controller: CustomerCarePageController,
  controllerAs: 'customer'
};